import type { IconName } from '@/ui/components/iconName';

import { beautifyCommands, formatCommand, minifyCommand } from './beautify/commands';
import {
  clearCommand,
  copyCommand,
  documentCommands,
  openExamplesCommand,
  pasteCommand,
} from './document/commands';
import { editorView } from './editor/view';
import { fixAll, lintCommands } from './lint/commands';
import type { Command, DocumentSnapshot, ViewId } from './types';

/**
 * THE SEAM — docs/ARCHITECTURE.md §4.9, §8, ADR-0014, ADR-0020.
 *
 * Two arrays. This is the only file in the repository allowed to know more than one feature
 * (gate 2 gives it its own ESLint zone), and it is the whole extension point: adding the Tree
 * view of §8 is `register(treeView)` on one line and `...treeCommands` on another. The tab
 * strip appears by itself because `TabBar` renders at `views.length > 1`; `src/ui/shell/**`
 * needs zero edits.
 *
 * ═══ WHY `views` IS NOT `readonly ViewDescriptor<unknown>[]` ═══
 *
 * `ViewDescriptor<T>` pairs `select(doc): T` with `load()`, whose module default is a
 * `ComponentType<{ view: T; api: FeatureApi }>`. The two halves must agree, and putting
 * several descriptors with DIFFERENT `T`s in one array erases that agreement — an existential
 * type, which TypeScript cannot name.
 *
 * ARCHITECTURE §4.9 writes `ViewDescriptor<any>[]`, and that is not available here:
 * `@typescript-eslint/no-explicit-any` is an error, and `eslint . --no-inline-config` means a
 * `// eslint-disable-next-line` is ignored rather than obeyed — the rule cannot be silenced,
 * only satisfied. `ViewDescriptor<unknown>` does not work either, and the reason is worth
 * stating because it looks like it should: `select` is fine (every `T` is an `unknown`), but
 * `load` is not — a component that requires `{ view: EditorTabView }` cannot stand in for one
 * that must accept `{ view: unknown }`, which is React props being contravariant.
 *
 * `RegisteredView` below is that erasure written out, and both halves widen rather than being
 * asserted, so `views` needs no cast at all:
 *
 *   · `select(doc): unknown`      — every `T` widens to `unknown`, so every descriptor fits.
 *   · `load(): Promise<{ default: unknown }>` — the module's default export is OPAQUE here.
 *
 * The panel type is erased to `unknown` rather than to a contravariant `ComponentType<{ view:
 * never }>`, which was the first attempt and does not work. `ComponentType` is a union with
 * `ComponentClass`, whose `defaultProps: Partial<P>` puts `P` in a COVARIANT position, so the
 * two arms of the union pull in opposite directions and no substitution for `P` can satisfy
 * both. Measured, not assumed: `tsc` rejected it, citing `defaultProps`.
 *
 * `unknown` makes the pair un-callable by anyone holding only a `RegisteredView`, which is
 * exactly right: re-pairing the two halves is a privilege, and it belongs to the composition
 * root. `src/app/viewProjection.ts` is the one place that does it, with the one assertion that
 * erasure costs and an argument for why it holds.
 */

/**
 * A `ViewDescriptor<T>` with `T` sealed inside it. Structurally satisfied by every descriptor
 * — the fields are listed rather than `Omit`ed so the variance above is visible where it is
 * relied upon.
 */
export interface RegisteredView {
  readonly id: ViewId;
  /** An i18n key, never a literal. */
  readonly labelKey: string;
  readonly icon: IconName;
  readonly beta?: boolean | undefined;
  /** Above this size the tab explains itself instead of loading (ADR-0020 §4). */
  readonly maxBytes?: number | undefined;
  /** Pure, synchronous, React-free. The result is opaque to everyone but the panel. */
  select(doc: DocumentSnapshot): unknown;
  /**
   * Lazy, and un-renderable through this type on purpose — see the note above. The thunk is
   * passed through untouched by `ViewHost`, which is what keeps a heavy tab free.
   */
  readonly load: () => Promise<{ readonly default: unknown }>;
}

/**
 * v1 registers exactly one view, so the tab strip renders nothing and `<main>` is a plain
 * region rather than a `tabpanel` (`ViewHost tabbed={views.length > 1}`).
 */
export const views: readonly RegisteredView[] = [editorView];

/**
 * Every action in the product, in toolbar order.
 *
 * The toolbar and the CodeMirror keymap are both derived from THIS array — `src/app/App.tsx`
 * projects it to `ToolbarItem`s, `src/app/keymap.ts` to key bindings — so a shortcut printed
 * on a button cannot disagree with the shortcut that fires it (ADR-0014). `keymap.test.ts`
 * asserts the other half: no two commands claim the same key.
 */
export const commands: readonly Command[] = [
  ...beautifyCommands, // format, minify
  ...documentCommands, // copy, paste, clear, openExamples
  ...lintCommands, // fixAll, applyFix, nextProblem, previousProblem
];

/**
 * The subset the ACTION BAR renders, in bar order (docs/DESIGN.md §6.2).
 *
 * Not every command is a button, and the three that are not are not an oversight:
 * `applyFix`, `nextProblem` and `previousProblem` are bound to the PROBLEM CURSOR, and each
 * already has a surface of its own — the Fix button on the selected row, and `F8` / `⇧F8`.
 * Putting "Next problem" on the toolbar would be a fourth control competing with a keyboard
 * contract the problems panel prints in its own footer.
 *
 * Placement is registry knowledge, so it lives in the registry: adding a command is still one
 * file, and the bar's ORDER — which drives the group hairlines in `Toolbar.tsx` — is stated
 * once, here, rather than being reconstructed in `src/app/App.tsx`.
 *
 * `commands` above stays the complete list, and it is the one the KEYMAP is derived from — so
 * a command that is not on the bar still has a working shortcut, which is the whole point of
 * `F8` existing at all.
 */
export const toolbarCommands: readonly Command[] = [
  formatCommand,
  minifyCommand,
  fixAll,
  copyCommand,
  pasteCommand,
  clearCommand,
  openExamplesCommand,
];
